import * as zod from "zod";

/**
 * @summary Get Tenant Metadata Endpoint
 */
export const GetTenantMetadataParams = zod.object({
  tenant_name: zod.string(),
});

export const getTenantMetadataResponseTenantNameMin = 3;
export const getTenantMetadataResponseTenantNameMax = 63;

export const getTenantMetadataResponseLongNameMin = 3;
export const getTenantMetadataResponseLongNameMax = 255;

export const GetTenantMetadataResponse = zod.object({
  tenant_name: zod
    .string()
    .min(getTenantMetadataResponseTenantNameMin)
    .max(getTenantMetadataResponseTenantNameMax),
  long_name: zod
    .string()
    .min(getTenantMetadataResponseLongNameMin)
    .max(getTenantMetadataResponseLongNameMax),
  typical_heats_per_day: zod.number(),
  small_mass_per_mass: zod.number(),
  rounding_units: zod.number(),
  local_timezone: zod.string(),
  max_crane_assignments: zod.int(),
  created_at: zod.iso.datetime({ offset: true }),
  latitude: zod.number(),
  longitude: zod.number(),
  heat_day_start_hour: zod.int(),
  heat_day_grouping_field: zod.enum(["start_time", "tap_time"]),
  small_mass_fraction_per_mass_fraction: zod.number(),
  impurity_units: zod.enum(["mass_fraction", "small_mass_fraction"]),
  default_mix_breakdown: zod.enum(["percentage", "mass", "small_mass"]),
  default_production_plan_source: zod.enum(["product_groups", "steel_grades"]),
  default_language: zod.string(),
  auto_search_types: zod.array(
    zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ])
  ),
  inventory_form_recipe_fanout: zod.array(
    zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ])
  ),
  feature_flags: zod.array(
    zod.enum([
      "fixed_mix_defaults",
      "material_availability_import",
      "search_context_groups",
      "electrical_energy_price",
      "plan_excel_download",
      "inventory_estimate_at_timestamp",
      "auto_backups",
      "show_all_edited_plans",
      "inventory_measurement",
      "inventory_measurement_with_locations",
      "inventory_upload_gateway",
      "inventory_measurement_with_estimate",
      "strategy_dashboard_inventory",
      "strategy_dashboard_consumption",
      "strategy_dashboard_production",
      "strategy_dashboard_production_pace",
      "strategy_dashboard_quality",
      "strategy_dashboard_cost",
      "strategy_dashboard_prices",
      "strategy_dashboard_heats",
      "strategy_dashboard_scrap_chef_usage",
      "scrap_chef_usage_detailed",
      "strategy_dashboard_deliveries",
      "strategy_dashboard_compliance",
      "strategy_dashboard_consumption_projection",
      "strategy_dashboard_editing",
      "strategy_dashboard_crew_comparison",
      "strategy_dashboard_schedule",
      "strategy_dashboard_savings",
      "scrap_eyes",
      "scrap_eyes_icon",
      "purchasing_plan_deploy",
      "heats_view_per_shift",
      "peak_shaving_scenarios",
      "cleanup_override",
      "mix_inventory_depletion",
      "mobile_ui",
      "live_recipe_card",
      "show_in_mission_control",
      "rail_car_movements",
      "per_basket_comparison",
      "show_charged_cost",
      "ranking_compliance_tab",
      "compliance_cost",
      "external_recipe_comparison",
      "compliance_per_heat",
      "strategy_dashboard_delivery_pace_percent",
      "material_overrides",
      "external_deployment_sync",
      "first_basket_material_cap",
      "layering_production_group_name",
      "bucket_layering_compliance",
      "likely_error_flagging",
      "targeted_review",
      "overview_current_recipe",
      "overview_deployed_plan",
      "overview_recent_searches",
      "overview_compliance",
      "operations",
      "optimiser",
      "operator_error_rate",
      "railcar_area_materials",
      "heat_video_crane_lanes",
      "mass_adjustment_wide_view",
    ])
  ),
  deploy_process: zod.enum([
    "edit_sync_and_sms",
    "sync_and_sms",
    "edit_and_send",
    "message",
    "none",
  ]),
  basket_tab_type: zod.enum(["basket_plot", "layering_table"]),
  mix_grouping: zod.enum(["copper_group", "grouping_name"]),
  impurity_plot_type: zod.enum(["box", "bar"]),
  message_type: zod.enum(["email", "sms"]),
  mix_quantity: zod.enum(["mass", "grabs"]),
  recipe_type: zod.enum(["bucket", "ratio"]),
  recipe_floor_source: zod.enum(["optimisation_result_set", "planned_mix_set"]),
  auth_provider: zod.enum(["google", "microsoft"]),
  layer_edit_mode: zod.enum(["none", "manual_only", "full"]),
  basket_layer_display_order: zod.enum(["top_first", "bottom_first"]),
  heat_override_mode: zod.enum(["disabled", "catalog", "delta"]),
  crane_driver_view: zod.enum(["none", "load_entry", "heat_recipes"]),
  compliance_report_overrides_section: zod
    .enum(["none", "reason_counts_by_crew", "override_list_by_crew"])
    .describe(
      "Content of the overrides section in a recipe-compliance report."
    ),
  heat_override_options: zod.array(
    zod.enum([
      "stable_heat_id_column",
      "product_group_column",
      "loading_status",
      "comment_required",
      "edit_history",
    ])
  ),
  heat_override_max_heats: zod.union([zod.int(), zod.null()]),
  compliance_page_version: zod.enum(["legacy", "modern"]),
  units: zod.array(
    zod.object({
      unit_type: zod
        .enum([
          "mass",
          "small_mass",
          "cost",
          "specific_cost",
          "electrical_energy_consumption",
          "mass_fraction",
          "yield",
          "volume",
          "density",
          "energy",
          "specific_energy_cost",
          "time",
          "small_mass_fraction",
        ])
        .describe("Enum of quantities requiring different units"),
      units: zod.string(),
    })
  ),
  translation_overrides: zod.record(zod.string(), zod.string()),
  manual_heat_overrides: zod.array(zod.string()),
});

/**
 * @summary List Tenant Names
 */
export const listTenantNamesQueryVisibilityDefault = `user`;

export const ListTenantNamesQueryParams = zod.object({
  tenant_group_name: zod.union([zod.string(), zod.null()]).optional(),
  visibility: zod
    .enum(["user", "all"])
    .default(listTenantNamesQueryVisibilityDefault),
});

export const ListTenantNamesResponse = zod.object({
  tenant_names: zod.array(zod.string()),
});

/**
 * @summary List Tenant Groups
 */
export const ListTenantGroupsResponse = zod.object({
  tenant_group_names: zod.array(zod.string()),
  tenant_group_long_names: zod.record(zod.string(), zod.string()),
});
